/**
 * Client-side workers: Web Worker first, optional server worker_threads via REST.
 */
import type { WorkerJobResult, WorkersApi } from "@arclog/module-sdk";

function authHeaders(): HeadersInit {
  const t = localStorage.getItem("arclog.token");
  return t
    ? { Authorization: "Bearer " + t, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" };
}

function runInline(source: string, input: unknown): WorkerJobResult {
  const started = performance.now();
  try {
    // eslint-disable-next-line no-new-func
    const fn = new Function("input", `"use strict"; return (async () => { ${source}\n })();`);
    // Note: async result resolved by caller path in run()
    return {
      ok: true,
      result: fn(input),
      backend: "inline",
      ms: Math.round(performance.now() - started),
    };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
      backend: "inline",
      ms: Math.round(performance.now() - started),
    };
  }
}

function runWebWorker(
  source: string,
  input: unknown,
  timeoutMs: number
): Promise<WorkerJobResult> {
  const started = performance.now();
  return new Promise((resolve) => {
    const blob = new Blob(
      [
        `
self.onmessage = async (ev) => {
  const { source, input } = ev.data || {};
  try {
    const fn = new Function("input", "return (async () => { " + source + "\\n })();");
    const result = await fn(input);
    self.postMessage({ ok: true, result });
  } catch (e) {
    self.postMessage({ ok: false, error: e && e.message ? String(e.message) : String(e) });
  }
};
`,
      ],
      { type: "application/javascript" }
    );
    const url = URL.createObjectURL(blob);
    const worker = new Worker(url);
    let settled = false;
    const done = (msg: WorkerJobResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      worker.terminate();
      URL.revokeObjectURL(url);
      resolve(msg);
    };
    const timer = setTimeout(() => {
      done({
        ok: false,
        error: `web-worker timeout after ${timeoutMs}ms`,
        backend: "web-worker",
        ms: Math.round(performance.now() - started),
      });
    }, timeoutMs);
    worker.onmessage = (ev) => {
      const d = ev.data || {};
      done({
        ok: !!d.ok,
        result: d.result,
        error: d.error,
        backend: "web-worker",
        ms: Math.round(performance.now() - started),
      });
    };
    worker.onerror = (err) => {
      done({
        ok: false,
        error: err.message || "web-worker error",
        backend: "web-worker",
        ms: Math.round(performance.now() - started),
      });
    };
    worker.postMessage({ source, input });
  });
}

async function runServerThread(
  source: string,
  input: unknown,
  timeoutMs: number
): Promise<WorkerJobResult> {
  const started = performance.now();
  try {
    const res = await fetch("/api/workers/run", {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify({ source, input, timeoutMs }),
    });
    const data = await res.json();
    if (!res.ok) {
      return {
        ok: false,
        error: data.error || res.statusText,
        backend: "worker-thread",
        ms: Math.round(performance.now() - started),
      };
    }
    return {
      ok: !!data.ok,
      result: data.result,
      error: data.error,
      backend: "worker-thread",
      ms: data.ms ?? Math.round(performance.now() - started),
    };
  } catch (e) {
    return {
      ok: false,
      error: e instanceof Error ? e.message : String(e),
      backend: "worker-thread",
      ms: Math.round(performance.now() - started),
    };
  }
}


export function createWorkersApi(): WorkersApi {
  const runOne = async (opts: {
    source: string;
    input?: unknown;
    backend?: "auto" | "web-worker" | "worker-thread" | "inline";
    timeoutMs?: number;
  }): Promise<WorkerJobResult> => {
      const source = String(opts.source || "");
      if (!source.trim()) {
        return { ok: false, error: "source required", backend: "inline" };
      }
      if (source.length > 200_000) {
        return { ok: false, error: "source too large", backend: "inline" };
      }
      const input = opts.input;
      const timeoutMs = Math.min(Math.max(opts.timeoutMs || 15_000, 100), 120_000);
      const backend = opts.backend || "auto";

      if (backend === "inline") {
        const r = runInline(source, input);
        if (r.ok && r.result && typeof (r.result as Promise<unknown>)?.then === "function") {
          try {
            const result = await (r.result as Promise<unknown>);
            return { ...r, result };
          } catch (e) {
            return {
              ok: false,
              error: e instanceof Error ? e.message : String(e),
              backend: "inline",
            };
          }
        }
        return r;
      }

      if (backend === "worker-thread") {
        return runServerThread(source, input, timeoutMs);
      }

      if (backend === "web-worker" || backend === "auto") {
        if (typeof Worker !== "undefined") {
          try {
            return await runWebWorker(source, input, timeoutMs);
          } catch {
            if (backend === "web-worker") {
              return {
                ok: false,
                error: "web-worker failed",
                backend: "web-worker",
              };
            }
            // auto fallback
          }
        }
        if (backend === "auto") {
          const server = await runServerThread(source, input, timeoutMs);
          if (server.ok || server.error !== "Failed to fetch") return server;
          const r = runInline(source, input);
          if (r.ok && r.result && typeof (r.result as Promise<unknown>)?.then === "function") {
            try {
              return { ...r, result: await (r.result as Promise<unknown>) };
            } catch (e) {
              return {
                ok: false,
                error: e instanceof Error ? e.message : String(e),
                backend: "inline",
              };
            }
          }
          return r;
        }
      }

      return runInline(source, input);
  };

  const suggestedConcurrency = () => {
    try {
      const hc = typeof navigator !== "undefined" ? navigator.hardwareConcurrency || 4 : 4;
      return Math.max(1, Math.min(8, hc));
    } catch {
      return 4;
    }
  };

  return {
    run: runOne,
    suggestedConcurrency,
    async runParallel(jobs, opts) {
      const list = Array.isArray(jobs) ? jobs : [];
      if (!list.length) return [];
      const limit = Math.max(
        1,
        Math.min(opts?.concurrency || suggestedConcurrency(), list.length, 16)
      );
      const results: WorkerJobResult[] = new Array(list.length);
      let next = 0;
      const workers = Array.from({ length: limit }, async () => {
        while (true) {
          const i = next++;
          if (i >= list.length) return;
          results[i] = await runOne(list[i] || { source: "return null;" });
        }
      });
      await Promise.all(workers);
      return results;
    },
  };
}
